/* MOD-CUSTOM: Quantity tier radio buttons -> render from product metafield quantity_option_group + sync into Flow product-form quantity */

(() => {
  const CONTAINER_SEL = "[data-custom-quantity-tiers]";
  const LIST_SEL = "[data-custom-quantity-tiers-list]";
  const DATA_SEL = "[data-custom-quantity-tiers-data]";
  const TIER_SEL = "[data-custom-quantity-tier]";
  const INIT_ATTR = "data-custom-quantity-tiers-initialized";
  const FIRST_TIME_DISCOUNT_SEL = "[data-custom-quantity-tiers-first-time-discount]";

  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === "function") {
      return Shopify.formatMoney(cents);
    }
    const currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "USD";
    return (cents / 100).toLocaleString(undefined, { style: "currency", currency });
  }

  function parseIntSafe(v) {
    const n = parseInt(String(v), 10);
    return Number.isFinite(n) ? n : null;
  }

  function parseFloatSafe(v) {
    const n = parseFloat(String(v));
    return Number.isFinite(n) ? n : null;
  }

  function clampQuantity(v) {
    const n = parseIntSafe(v);
    return n && n > 0 ? n : 1;
  }

  function dispatchInputAndChange(el) {
    try {
      el.dispatchEvent(new Event("input", { bubbles: true }));
      el.dispatchEvent(new Event("change", { bubbles: true }));
    } catch (_) {}
  }

  function findSectionRoot(container) {
    return (
      container.closest('[id^="shopify-section-"]') ||
      container.closest("[data-section-id]")?.closest('[id^="shopify-section-"]') ||
      container.closest("main") ||
      document
    );
  }

  function getSectionId(container, form) {
    const fromData = container.getAttribute("data-section-id");
    if (fromData) return fromData;

    const pf = container.closest("product-form");
    if (pf?.dataset?.sectionId) return pf.dataset.sectionId;

    const fromHidden = form?.querySelector('input[name="section-id"]')?.value;
    if (fromHidden) return fromHidden;

    return null;
  }

  function findAddToCartForm(container) {
    const formId = container.dataset.formId;
    if (formId) {
      const found = document.getElementById(formId);
      if (found) return found;
    }

    const sectionRoot = findSectionRoot(container);

    const nearestPF = container.closest("product-form");
    if (nearestPF) {
      const found = nearestPF.querySelector('form[action="/cart/add"]');
      if (found) return found;
    }

    const forms = sectionRoot.querySelectorAll(
      'product-form form[action="/cart/add"], form[action="/cart/add"].shopify-product-form, form[action="/cart/add"]'
    );
    if (!forms.length) return null;
    if (forms.length === 1) return forms[0];

    const sidGuess = container.getAttribute("data-section-id") || null;
    if (sidGuess) {
      for (const form of forms) {
        const sid = form.querySelector('input[name="section-id"]')?.value;
        if (sid && sid === sidGuess) return form;

        const pf = form.closest("product-form");
        if (pf?.dataset?.sectionId === sidGuess) return form;
      }
    }

    return forms[0];
  }

  function ensureHiddenQuantityInput(form) {
    let el = form.querySelector('input[name="quantity"]');
    if (el) return el;

    el = document.createElement("input");
    el.type = "hidden";
    el.name = "quantity";
    el.value = "1";
    form.appendChild(el);
    return el;
  }

  function findUiQuantityInput(container, sectionId) {
    if (!sectionId) return null;
    const root = findSectionRoot(container);
    return root.querySelector(
      `quantity-input[data-section-id="${sectionId}"] input[name="quantity-js"]`
    );
  }

  function setActive(container, quantity) {
    container.querySelectorAll(TIER_SEL).forEach((tierEl) => {
      const tierQuantity = clampQuantity(tierEl.dataset.customQuantityTier);
      const isActive = tierQuantity === quantity;
      tierEl.classList.toggle("custom-quantity-tiers__item--active", isActive);
      const radio = tierEl.querySelector('input[type="radio"]');
      if (radio) radio.checked = isActive;
    });
  }

  function renderTiers(container, optionData, extraDiscountCents = 0, cartSubtotalCents = 0) {
    const list = container.querySelector(LIST_SEL);
    if (!list) return;

    if (!optionData || !Array.isArray(optionData.options) || optionData.options.length === 0) {
      list.innerHTML = "";
      return;
    }

    const options = [...optionData.options].sort((a, b) => (a.quantity || 0) - (b.quantity || 0));
    const contentsPerUnit = Math.max(1, parseIntSafe(optionData.unitsPerItem) || 1);
    const containerId = container.dataset.formId || Math.random().toString(36).slice(2);
    const freeShippingMinCents = Math.round((parseFloatSafe(optionData.freeShippingMinAmountDollars) || 0) * 100);
    const estimatedShippingCents = Math.round((parseFloatSafe(optionData.estimatedShippingCostDollars) || 0) * 100);

    list.innerHTML = options
      .map((opt, optionIndex) => {
        const quantity = Math.max(1, parseIntSafe(opt.quantity) || 1);
        const discountDecimal = Math.min(1, Math.max(0, parseFloatSafe(opt.discountPercent) || 0));
        const discountPercent = Math.round(discountDecimal * 100);

        const totalContents = quantity * contentsPerUnit;
        const originalPerContentCents = Math.round(optionData.price / contentsPerUnit);
        const tierDiscountedTotalCents = Math.round(optionData.price * quantity * (1 - discountDecimal));
        const effectiveTotalCents = Math.max(0, tierDiscountedTotalCents - extraDiscountCents);
        const shippingApplies = freeShippingMinCents > 0 && (cartSubtotalCents + effectiveTotalCents) < freeShippingMinCents;
        const effectiveTotalWithShippingCents = shippingApplies ? effectiveTotalCents + estimatedShippingCents : effectiveTotalCents;
        const discountedPerContentCents = Math.round(effectiveTotalWithShippingCents / totalContents);

        const unitLabel = totalContents === 1 ? optionData.unitSingular : optionData.unitPlural;
        const suffix = (optionData.suffix || "").trim();
        const suffixText = suffix ? ` ${suffix}` : "";

        const marketingTagline = (opt.marketingTagline || "").trim();
        const hasTagline = marketingTagline.length > 0;

        const taglineHtml = hasTagline
          ? `<span class="custom-quantity-tiers__tagline-badge" aria-label="${marketingTagline}">${marketingTagline}</span>`
          : "";

        const badges = [];
        if (discountPercent > 0) {
          badges.push({ modifier: "discount", text: `Save ${discountPercent}%` });
        }
        if (extraDiscountCents > 0) {
          badges.push({ modifier: "extra-discount", text: `${formatMoney(extraDiscountCents)} off` });
        }
        if (!shippingApplies && freeShippingMinCents > 0) {
          badges.push({ modifier: "free-shipping", text: "Free Shipping" });
        }
        const badgeRowHtml = badges.length > 0
          ? `<span class="custom-quantity-tiers__badge-row">${
              badges.map((badge, badgeIndex) =>
                `<span class="custom-quantity-tiers__badge custom-quantity-tiers__badge--${badge.modifier}">${badgeIndex > 0 ? "+ " : ""}${badge.text}</span>`
              ).join("")
            }</span>`
          : "";

        const showOriginalPrice = discountPercent > 0 || extraDiscountCents > 0;
        const originalPriceHtml = showOriginalPrice
          ? `<span class="custom-quantity-tiers__original-price">${formatMoney(originalPerContentCents)}${suffixText}</span>`
          : "";

        const shippingNoteHtml = shippingApplies && estimatedShippingCents > 0
          ? `<span class="custom-quantity-tiers__shipping-note">* incl. shipping</span>`
          : "";

        const radioId = `quantity-tier-${containerId}-${optionIndex}`;

        return `
          <div
            class="custom-quantity-tiers__item${hasTagline ? ' custom-quantity-tiers__item--has-tagline' : ''}"
            data-custom-quantity-tier="${quantity}"
          >
            ${taglineHtml}
            <label class="custom-quantity-tiers__item-inner" for="${radioId}">
              <span class="custom-quantity-tiers__radio-wrap">
                <input
                  type="radio"
                  id="${radioId}"
                  name="quantity-tier-${containerId}"
                  value="${quantity}"
                  class="custom-quantity-tiers__radio"
                />
                <span class="custom-quantity-tiers__radio-dot" aria-hidden="true"></span>
              </span>
              <span class="custom-quantity-tiers__main">
                ${badgeRowHtml}
                <span class="custom-quantity-tiers__quantity-label">${totalContents} ${unitLabel}</span>
              </span>
              <span class="custom-quantity-tiers__pricing">
                ${originalPriceHtml}
                <span class="custom-quantity-tiers__current-price">${formatMoney(discountedPerContentCents)}${suffixText}${shippingNoteHtml ? "*" : ""}</span>
                ${shippingNoteHtml}
              </span>
            </label>
          </div>
        `;
      })
      .join("");
  }

  function fetchCartSubtotal() {
    return fetch("/cart.js")
      .then((response) => response.json())
      .then((cart) => cart.total_price || 0)
      .catch(() => 0);
  }

  function initContainer(container) {
    if (container.hasAttribute(INIT_ATTR)) return;
    container.setAttribute(INIT_ATTR, "true");

    const dataEl = container.querySelector(DATA_SEL);
    if (!dataEl) return;

    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch {
      return;
    }

    const form = findAddToCartForm(container);
    if (!form) return;

    const quantityHidden = ensureHiddenQuantityInput(form);
    const sectionId = getSectionId(container, form);
    const quantityUi = findUiQuantityInput(container, sectionId);

    let selectedQuantity = clampQuantity(
      quantityHidden.value || (quantityUi ? quantityUi.value : 1)
    );
    let activeExtraDiscountCents = 0;
    let cartSubtotalCents = 0;

    function rerenderTiers() {
      renderTiers(container, data, activeExtraDiscountCents, cartSubtotalCents);
      bindTierClicks();
      setActive(container, selectedQuantity);
    }

    function setQuantity(quantity) {
      const n = clampQuantity(quantity);
      selectedQuantity = n;

      quantityHidden.value = String(n);
      dispatchInputAndChange(quantityHidden);

      if (quantityUi) {
        quantityUi.value = String(n);
        dispatchInputAndChange(quantityUi);
      }

      setActive(container, n);
    }

    function syncActive() {
      const currentQuantity = clampQuantity(quantityHidden.value || (quantityUi ? quantityUi.value : 1));
      setActive(container, currentQuantity);
    }

    function bindTierClicks() {
      container.querySelectorAll(TIER_SEL).forEach((tierEl) => {
        tierEl.addEventListener("click", () => setQuantity(tierEl.dataset.customQuantityTier));
      });
    }

    quantityHidden.addEventListener("input", syncActive);
    quantityHidden.addEventListener("change", syncActive);
    if (quantityUi) {
      quantityUi.addEventListener("input", syncActive);
      quantityUi.addEventListener("change", syncActive);
    }

    if (window.eventBus) {
      window.eventBus.on("cart:added", (event) => {
        if (event.sectionId && event.sectionId !== sectionId) return;
        setTimeout(() => {
          quantityHidden.value = String(selectedQuantity);
        }, 0);
        fetchCartSubtotal().then((subtotal) => {
          cartSubtotalCents = subtotal;
          rerenderTiers();
        });
      });

      window.eventBus.on("update:cart:drawer", () => {
        fetchCartSubtotal().then((subtotal) => {
          cartSubtotalCents = subtotal;
          rerenderTiers();
        });
      });
    }

    form.addEventListener("submit", () => {
      const quantityInput = form.querySelector('input[name="quantity"]');
      if (quantityInput) quantityInput.value = String(clampQuantity(quantityHidden.value));
    }, true);

    // Initial render, then re-render once cart subtotal is known
    rerenderTiers();
    fetchCartSubtotal().then((subtotal) => {
      cartSubtotalCents = subtotal;
      rerenderTiers();
    });

    // First-time discount checkbox
    const discountLabel = container.querySelector(FIRST_TIME_DISCOUNT_SEL);
    const discountCheckbox = discountLabel?.querySelector('input[type="checkbox"]');

    if (discountCheckbox) {
      const discountAmountCents = Math.round(
        (parseFloatSafe(discountCheckbox.dataset.discountAmountDollars) || 0) * 100
      );

      function applyDiscountState() {
        activeExtraDiscountCents = discountCheckbox.checked ? discountAmountCents : 0;
        rerenderTiers();
      }

      discountCheckbox.addEventListener("change", applyDiscountState);

      if (discountCheckbox.checked) {
        applyDiscountState();
      }
    }
  }

  function boot(root = document) {
    root.querySelectorAll(CONTAINER_SEL).forEach(initContainer);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot(), { once: true });
  } else {
    boot();
  }

  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.(CONTAINER_SEL)) initContainer(node);
        node.querySelectorAll?.(CONTAINER_SEL).forEach(initContainer);
      }
    }
  });
  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
